import { Topic, LearningPath, CurriculumStructure } from '../../types/curriculum';

// Color Theory Topic Taxonomy
export const colorTheoryTopics: Record<string, Topic> = {
  // Root Level Topics
  'foundations': {
    id: 'foundations',
    name: 'Color Foundations',
    description: 'Fundamental concepts of color theory and basic color principles',
    level: 0,
    order: 1,
    icon: 'palette',
    color: '#3B82F6',
    estimatedHours: 8,
    difficulty: 'beginner',
    prerequisites: [],
    learningObjectives: [
      'Understand the color wheel and its components',
      'Learn basic color terminology and concepts',
      'Recognize primary, secondary, and tertiary colors',
      'Understand color properties (hue, saturation, value)'
    ],
    skills: ['color-identification', 'color-wheel-understanding', 'basic-color-theory'],
    tags: ['beginner', 'foundations', 'color-wheel', 'basics']
  },

  'harmony': {
    id: 'harmony',
    name: 'Color Harmony',
    description: 'Understanding color relationships and harmonious combinations',
    level: 0,
    order: 2,
    icon: 'sparkles',
    color: '#10B981',
    estimatedHours: 12,
    difficulty: 'intermediate',
    prerequisites: ['foundations'],
    learningObjectives: [
      'Master color harmony schemes',
      'Create balanced color palettes',
      'Understand color relationships and interactions',
      'Apply harmony principles in design'
    ],
    skills: ['color-harmony', 'palette-creation', 'color-relationships'],
    tags: ['intermediate', 'harmony', 'palettes', 'design']
  },

  'psychology': {
    id: 'psychology',
    name: 'Color Psychology',
    description: 'The emotional and cultural impact of colors in design',
    level: 0,
    order: 3,
    icon: 'brain',
    color: '#8B5CF6',
    estimatedHours: 10,
    difficulty: 'intermediate',
    prerequisites: ['foundations'],
    learningObjectives: [
      'Understand emotional responses to colors',
      'Learn cultural color associations',
      'Apply psychological principles in branding',
      'Consider accessibility in color choices'
    ],
    skills: ['color-psychology', 'brand-colors', 'cultural-awareness', 'accessibility'],
    tags: ['intermediate', 'psychology', 'branding', 'accessibility']
  },

  'application': {
    id: 'application',
    name: 'Practical Application',
    description: 'Applying color theory in real-world design scenarios',
    level: 0,
    order: 4,
    icon: 'brush',
    color: '#F59E0B',
    estimatedHours: 15,
    difficulty: 'advanced',
    prerequisites: ['foundations', 'harmony', 'psychology'],
    learningObjectives: [
      'Apply color theory to digital design',
      'Create effective color systems',
      'Consider technical constraints and accessibility',
      'Develop professional color workflows'
    ],
    skills: ['digital-design', 'color-systems', 'accessibility-compliance', 'workflow-optimization'],
    tags: ['advanced', 'practical', 'design-systems', 'accessibility']
  },

  // Subcategory Topics
  'color-wheel': {
    id: 'color-wheel',
    name: 'The Color Wheel',
    description: 'Understanding the color wheel structure and color relationships',
    level: 1,
    order: 1,
    parentId: 'foundations',
    icon: 'circle',
    color: '#3B82F6',
    estimatedHours: 3,
    difficulty: 'beginner',
    prerequisites: [],
    learningObjectives: [
      'Identify primary, secondary, and tertiary colors',
      'Understand color wheel construction',
      'Learn color positioning and relationships'
    ],
    skills: ['color-wheel-navigation', 'color-identification'],
    tags: ['color-wheel', 'primary-colors', 'secondary-colors']
  },

  'color-properties': {
    id: 'color-properties',
    name: 'Color Properties',
    description: 'Understanding hue, saturation, value, and other color dimensions',
    level: 1,
    order: 2,
    parentId: 'foundations',
    icon: 'sliders',
    color: '#3B82F6',
    estimatedHours: 3,
    difficulty: 'beginner',
    prerequisites: [],
    learningObjectives: [
      'Define hue, saturation, and value',
      'Understand color temperature',
      'Learn color dimensions and measurements'
    ],
    skills: ['color-dimensions', 'color-measurement', 'color-temperature'],
    tags: ['hsv', 'color-properties', 'color-dimensions']
  },

  'color-models': {
    id: 'color-models',
    name: 'Color Models',
    description: 'RGB, CMYK, HSL, and other color models for different mediums',
    level: 1,
    order: 3,
    parentId: 'foundations',
    icon: 'layers',
    color: '#3B82F6',
    estimatedHours: 2,
    difficulty: 'beginner',
    prerequisites: [],
    learningObjectives: [
      'Understand different color models',
      'Know when to use RGB vs CMYK',
      'Convert between color models'
    ],
    skills: ['color-models', 'rgb-cmyk', 'color-conversion'],
    tags: ['rgb', 'cmyk', 'hsl', 'color-models']
  },

  'complementary-harmony': {
    id: 'complementary-harmony',
    name: 'Complementary Colors',
    description: 'Using opposite colors for contrast and emphasis',
    level: 1,
    order: 1,
    parentId: 'harmony',
    icon: 'contrast',
    color: '#EF4444',
    estimatedHours: 3,
    difficulty: 'intermediate',
    prerequisites: ['color-wheel'],
    learningObjectives: [
      'Identify complementary color pairs',
      'Create high-contrast combinations',
      'Use complementary colors effectively'
    ],
    skills: ['complementary-colors', 'contrast-design', 'color-pairing'],
    tags: ['complementary', 'contrast', 'color-pairs']
  },

  'analogous-harmony': {
    id: 'analogous-harmony',
    name: 'Analogous Colors',
    description: 'Creating harmonious combinations with adjacent colors',
    level: 1,
    order: 2,
    parentId: 'harmony',
    icon: 'gradient',
    color: '#10B981',
    estimatedHours: 3,
    difficulty: 'intermediate',
    prerequisites: ['color-wheel'],
    learningObjectives: [
      'Identify analogous color schemes',
      'Create smooth color transitions',
      'Build harmonious color palettes'
    ],
    skills: ['analogous-colors', 'harmony-design', 'color-transitions'],
    tags: ['analogous', 'harmony', 'transitions']
  },

  'triadic-harmony': {
    id: 'triadic-harmony',
    name: 'Triadic Colors',
    description: 'Balanced three-color schemes for dynamic designs',
    level: 1,
    order: 3,
    parentId: 'harmony',
    icon: 'triangle',
    color: '#F59E0B',
    estimatedHours: 3,
    difficulty: 'intermediate',
    prerequisites: ['color-wheel'],
    learningObjectives: [
      'Create triadic color schemes',
      'Balance three-color combinations',
      'Apply triadic harmony in design'
    ],
    skills: ['triadic-colors', 'color-balance', 'scheme-creation'],
    tags: ['triadic', 'three-colors', 'balance']
  },

  'emotional-impact': {
    id: 'emotional-impact',
    name: 'Emotional Impact',
    description: 'How colors affect emotions and user behavior',
    level: 1,
    order: 1,
    parentId: 'psychology',
    icon: 'heart',
    color: '#EC4899',
    estimatedHours: 4,
    difficulty: 'intermediate',
    prerequisites: ['color-properties'],
    learningObjectives: [
      'Understand color-emotion associations',
      'Apply emotional color theory',
      'Consider user emotional responses'
    ],
    skills: ['emotional-design', 'user-psychology', 'color-emotions'],
    tags: ['emotions', 'psychology', 'user-experience']
  },

  'cultural-significance': {
    id: 'cultural-significance',
    name: 'Cultural Significance',
    description: 'Color meanings across different cultures and contexts',
    level: 1,
    order: 2,
    parentId: 'psychology',
    icon: 'globe',
    color: '#8B5CF6',
    estimatedHours: 3,
    difficulty: 'intermediate',
    prerequisites: ['color-properties'],
    learningObjectives: [
      'Learn cultural color associations',
      'Design for global audiences',
      'Avoid cultural color mistakes'
    ],
    skills: ['cultural-design', 'global-audience', 'cultural-awareness'],
    tags: ['culture', 'global', 'international']
  },

  'brand-colors': {
    id: 'brand-colors',
    name: 'Brand Colors',
    description: 'Color strategy for branding and marketing',
    level: 1,
    order: 3,
    parentId: 'psychology',
    icon: 'tag',
    color: '#059669',
    estimatedHours: 3,
    difficulty: 'intermediate',
    prerequisites: ['emotional-impact'],
    learningObjectives: [
      'Develop brand color strategies',
      'Create memorable color identities',
      'Apply color psychology to branding'
    ],
    skills: ['brand-strategy', 'color-identity', 'marketing-colors'],
    tags: ['branding', 'marketing', 'identity']
  },

  'digital-design': {
    id: 'digital-design',
    name: 'Digital Design',
    description: 'Color theory for websites, apps, and digital interfaces',
    level: 1,
    order: 1,
    parentId: 'application',
    icon: 'monitor',
    color: '#0EA5E9',
    estimatedHours: 5,
    difficulty: 'advanced',
    prerequisites: ['complementary-harmony', 'analogous-harmony', 'brand-colors'],
    learningObjectives: [
      'Apply color theory to UI/UX design',
      'Create effective digital color systems',
      'Consider screen and device constraints'
    ],
    skills: ['ui-design', 'color-systems', 'digital-colors'],
    tags: ['ui', 'ux', 'digital', 'interfaces']
  },

  'accessibility': {
    id: 'accessibility',
    name: 'Color Accessibility',
    description: 'Creating inclusive color choices for all users',
    level: 1,
    order: 2,
    parentId: 'application',
    icon: 'universal-access',
    color: '#059669',
    estimatedHours: 4,
    difficulty: 'advanced',
    prerequisites: ['complementary-harmony', 'emotional-impact'],
    learningObjectives: [
      'Understand WCAG color requirements',
      'Test color contrast and accessibility',
      'Design for colorblind users'
    ],
    skills: ['wcag-compliance', 'contrast-testing', 'inclusive-design'],
    tags: ['accessibility', 'wcag', 'inclusive', 'colorblind']
  },

  'print-design': {
    id: 'print-design',
    name: 'Print Design',
    description: 'Color considerations for print and physical media',
    level: 1,
    order: 3,
    parentId: 'application',
    icon: 'printer',
    color: '#DC2626',
    estimatedHours: 3,
    difficulty: 'advanced',
    prerequisites: ['color-models', 'triadic-harmony'],
    learningObjectives: [
      'Apply color theory to print design',
      'Understand printing color constraints',
      'Manage color profiles for print'
    ],
    skills: ['print-design', 'color-profiles', 'cmyk-workflow'],
    tags: ['print', 'cmyk', 'physical-media']
  },

  'color-workflows': {
    id: 'color-workflows',
    name: 'Color Workflows',
    description: 'Professional color management and workflow optimization',
    level: 1,
    order: 4,
    parentId: 'application',
    icon: 'workflow',
    color: '#7C3AED',
    estimatedHours: 3,
    difficulty: 'advanced',
    prerequisites: ['digital-design', 'accessibility'],
    learningObjectives: [
      'Establish professional color workflows',
      'Manage color consistency across projects',
      'Optimize color processes'
    ],
    skills: ['workflow-management', 'color-consistency', 'process-optimization'],
    tags: ['workflow', 'professional', 'consistency']
  }
};

// Learning Paths
export const colorTheoryLearningPaths: Record<string, LearningPath> = {
  'beginner-color-foundations': {
    id: 'beginner-color-foundations',
    name: 'Color Theory Foundations',
    description: 'Complete beginner\'s guide to understanding and working with colors',
    category: 'foundations',
    difficulty: 'beginner',
    estimatedDuration: '2 weeks',
    topics: ['color-wheel', 'color-properties', 'color-models'],
    outcomes: [
      'Confidently identify and work with colors',
      'Understand basic color theory principles',
      'Create simple color combinations',
      'Apply color concepts to basic design projects'
    ],
    badge: {
      name: 'Color Foundations',
      icon: 'palette',
      description: 'Mastered the fundamentals of color theory'
    },
    requirements: {
      minQuizScore: 70,
      completionThreshold: 80
    }
  },

  'intermediate-color-harmony': {
    id: 'intermediate-color-harmony',
    name: 'Color Harmony & Design',
    description: 'Master color relationships and create harmonious designs',
    category: 'practical',
    difficulty: 'intermediate',
    estimatedDuration: '3 weeks',
    topics: ['complementary-harmony', 'analogous-harmony', 'triadic-harmony', 'emotional-impact'],
    outcomes: [
      'Create balanced color palettes',
      'Apply harmony principles effectively',
      'Understand emotional color impact',
      'Design with color confidence'
    ],
    badge: {
      name: 'Color Harmony Expert',
      icon: 'sparkles',
      description: 'Skilled in creating harmonious color combinations'
    },
    requirements: {
      minQuizScore: 75,
      completionThreshold: 85
    }
  },

  'advanced-color-professional': {
    id: 'advanced-color-professional',
    name: 'Professional Color Design',
    description: 'Advanced color theory for professional design applications',
    category: 'advanced',
    difficulty: 'advanced',
    estimatedDuration: '4 weeks',
    topics: ['brand-colors', 'cultural-significance', 'digital-design', 'accessibility', 'print-design', 'color-workflows'],
    outcomes: [
      'Design professional color systems',
      'Create accessible and inclusive designs',
      'Manage color workflows efficiently',
      'Apply color theory across multiple media'
    ],
    badge: {
      name: 'Color Design Professional',
      icon: 'award',
      description: 'Expert in professional color design and management'
    },
    requirements: {
      minQuizScore: 80,
      completionThreshold: 90
    }
  },

  'specialized-accessibility': {
    id: 'specialized-accessibility',
    name: 'Color Accessibility Specialist',
    description: 'Focus on creating accessible and inclusive color designs',
    category: 'specialized',
    difficulty: 'advanced',
    estimatedDuration: '2 weeks',
    topics: ['accessibility', 'emotional-impact', 'digital-design'],
    outcomes: [
      'Master WCAG color requirements',
      'Test and validate color accessibility',
      'Design for users with color vision deficiencies',
      'Create inclusive digital experiences'
    ],
    badge: {
      name: 'Accessibility Specialist',
      icon: 'universal-access',
      description: 'Expert in accessible color design'
    },
    requirements: {
      minQuizScore: 85,
      completionThreshold: 95
    }
  }
};

// Complete Curriculum Structure
export const colorTheoryCurriculum: CurriculumStructure = {
  topics: colorTheoryTopics,
  learningPaths: colorTheoryLearningPaths,
  skillFramework: {
    categories: {
      'fundamentals': {
        name: 'Color Fundamentals',
        description: 'Basic color theory concepts and principles',
        skills: ['color-identification', 'color-wheel-understanding', 'basic-color-theory', 'color-dimensions', 'color-measurement']
      },
      'harmony': {
        name: 'Color Harmony',
        description: 'Creating balanced and harmonious color combinations',
        skills: ['color-harmony', 'palette-creation', 'color-relationships', 'complementary-colors', 'analogous-colors', 'triadic-colors']
      },
      'psychology': {
        name: 'Color Psychology',
        description: 'Understanding emotional and cultural impact of colors',
        skills: ['color-psychology', 'brand-colors', 'cultural-awareness', 'emotional-design', 'user-psychology']
      },
      'application': {
        name: 'Practical Application',
        description: 'Applying color theory in real-world scenarios',
        skills: ['digital-design', 'color-systems', 'accessibility-compliance', 'workflow-optimization', 'ui-design']
      }
    },
    skillLevels: {
      '1': {
        level: 1,
        name: 'Novice',
        description: 'Basic understanding of color concepts',
        criteria: ['Can identify primary colors', 'Understands basic color wheel', 'Knows simple color terms']
      },
      '2': {
        level: 2,
        name: 'Beginner',
        description: 'Working knowledge of fundamental color theory',
        criteria: ['Can create simple color schemes', 'Understands color properties', 'Applies basic harmony principles']
      },
      '3': {
        level: 3,
        name: 'Intermediate',
        description: 'Confident application of color theory',
        criteria: ['Creates balanced color palettes', 'Understands color psychology', 'Applies harmony effectively']
      },
      '4': {
        level: 4,
        name: 'Advanced',
        description: 'Expert color design skills',
        criteria: ['Designs complex color systems', 'Considers accessibility', 'Manages color workflows']
      },
      '5': {
        level: 5,
        name: 'Expert',
        description: 'Mastery of color theory and application',
        criteria: ['Creates innovative color solutions', 'Teaches color theory effectively', 'Leads color strategy']
      }
    }
  },
  metadata: {
    version: '1.0.0',
    lastUpdated: new Date().toISOString(),
    totalTopics: Object.keys(colorTheoryTopics).length,
    totalLearningPaths: Object.keys(colorTheoryLearningPaths).length,
    estimatedTotalHours: Object.values(colorTheoryTopics).reduce((sum, topic) => sum + topic.estimatedHours, 0)
  }
};
